import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { entrepotActions } from "../../sagas/entrepotSlice";
import { Button, Card, CardBody, Col, Container, Row, Spinner, Table, Badge, InputGroup, InputGroupText, Input } from "reactstrap";
import { FaCubes, FaFileExcel, FaFileUpload, FaSearch, FaTimes, FaWarehouse } from "react-icons/fa";
import Select from "react-select";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { stockActions } from "../../sagas/stockSlice";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

const EntrepotCard = ({ entrepot, active, onClick }) => {
    return (
        <Card
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
            className={`mb-3 shadow-sm ${active ? "border border-2 border-primary" : "border-0"}`}
            style={{ cursor: "pointer", transition: "transform .15s ease-in-out" }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
        >
            <CardBody className="d-flex align-items-center">
                <div className="me-3 d-flex align-items-center justify-content-center rounded-circle"
                    style={{ width: 44, height: 44, background: "rgba(13,110,253,.1)" }}>
                    <FaWarehouse className="text-primary" />
                </div>
                <div className="flex-grow-1">
                    <div className="fw-semibold">
                        {entrepot.label ?? `${entrepot.libelle}`}
                    </div>
                </div>
                {active && <Badge color="primary">Sélectionné</Badge>}
            </CardBody>
        </Card>
    );
};

const StockPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { stocks, loading: stockLoading, error } = useSelector((state) => state.stock);
    const { entrepots, loading: entrepotLoading } = useSelector((state) => state.entrepots);
    const fileInputRef = useRef(null);
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedEntrepot, setSelectedEntrepot] = useState(null);


    // Fetch stocks whenever selection changes
    useEffect(() => {
        dispatch(entrepotActions.list());
    }, []);


    useEffect(() => {
        if (selectedEntrepot) {
            dispatch(stockActions.listStocks({ entrepotId: selectedEntrepot?.value }));
        }
    }, [selectedEntrepot])

    useEffect(() => {
        const id = setTimeout(() => setSearchTerm(searchInput.trim()), 200);
        return () => clearTimeout(id);
    }, [searchInput]);

    const entrepotOptions = useMemo(
        () =>
            (entrepots ?? []).map((entrepot) => ({
                value: entrepot.id,
                label: `${entrepot.code} - ${entrepot.libelle}`,
                raw: entrepot,
            })),
        [entrepots]
    );

    const allOption = { value: null, label: t("common.allWarehouses") || "Tous les entrepôts" };

    // simple case-insensitive contains
    const matches = useCallback((value, q) => {
        if (!q) return true;
        if (!value) return false;
        return String(value).toLowerCase().includes(q.toLowerCase());
    }, []);

    const refrechStocks = () => {
        dispatch(stockActions.listStocks({
            entrepotId: selectedEntrepot ? selectedEntrepot.value : null,
        }))
    }

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const allowedTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel'
            ];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire({
                    icon: 'error',
                    title: t('product.invalidFileType'),
                    text: t('product.excelOnly'),
                });
                return;
            }

            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('entrepotId', selectedEntrepot?.value);

                let payload = {
                    formData: formData,
                    onSuccess: () => {
                        refrechStocks();
                        Swal.fire({
                            icon: 'success',
                            title: t('stock.uploadSuccess'),
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#198754'
                        });
                    },
                }
                dispatch(stockActions.uploadStocks(payload));

            } catch (error) {
                console.error('Error parsing Excel:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Erreur!',
                    text: "Erreur lors de l'importation des produits",
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#dc3545'
                });
            } finally {
                // Reset file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        }
    };

    const filteredStocks = useMemo(() => {
        if (!stocks) return [];
        if (!searchTerm) return stocks;
        return stocks.filter((s) => {
            return (
                matches(s.articleNom, searchTerm) ||
                matches(s.entrepotNom, searchTerm)
                // add more fields if needed:
                // || matches(s.articleCode, searchTerm)
            );
        });
    }, [stocks, searchTerm, matches]);

    const handleExportExcel = useCallback(() => {
        const now = new Date();
        const datePart = now.toISOString().slice(0, 19).replace(/[:T]/g, "-");

        // En-têtes localisés
        const HEAD_ENTREPOT = t("entrepot.libelle") || "Entrepôt";
        const HEAD_ARTICLE = t("product.description") || "Article";
        const HEAD_QTE = t("product.stockDisponible") || "Stock disponible";

        // Transformer les données visibles (filteredStocks) en lignes simples
        const data = filteredStocks.map((s) => ({
            [HEAD_ENTREPOT]: s.entrepotNom ?? "",
            [HEAD_ARTICLE]: s.articleNom ?? "",
            [HEAD_QTE]: s.quantiteDisponible ?? 0,
        }));

        // Créer feuille & classeur
        const ws = XLSX.utils.json_to_sheet(data, { skipHeader: false });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Stocks");

        // Auto-width colonnes (basé sur la longeur max des cellules)
        const headers = [HEAD_ENTREPOT, HEAD_ARTICLE, HEAD_QTE];
        const colWidths = headers.map((h) => ({ wch: Math.max(h.length, 12) }));
        data.forEach((row) => {
            headers.forEach((h, idx) => {
                const cellLen = String(row[h] ?? "").length;
                if (cellLen + 2 > (colWidths[idx].wch || 10)) colWidths[idx].wch = cellLen + 2;
            });
        });
        ws["!cols"] = colWidths;

        // Nom de fichier contextualisé par entrepôt + date
        const entrepotLabel =
            selectedEntrepot?.label ||
            allOption.label ||
            "tous-entrepots";
        const safeLabel = String(entrepotLabel).toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "");
        const filename = `stocks_${safeLabel}_${datePart}.xlsx`;

        // Écrire et déclencher le téléchargement
        XLSX.writeFile(wb, filename);
    }, [filteredStocks, selectedEntrepot, t]);

    const handleClickCard = useCallback(
        (id) => {
            if (id === null) {
                setSelectedEntrepot(null);
            } else {
                const found = entrepotOptions.find((o) => o.value === id);
                setSelectedEntrepot(found ? { value: found.value, label: found.label } : null);
            }
        },
        [entrepotOptions]
    );

    const loading = stockLoading || entrepotLoading;

    return (
        <>
            {loading ? (
                <div className="text-center py-5">
                    <Spinner color="primary" />
                    <div className="mt-2 text-muted">{t("common.loading")}</div>
                </div>
            ) : (
                <Container fluid>
                    {/* Header */}
                    <Row className="mb-4">
                        <Col>
                            <Card className="shadow-sm">
                                <CardBody className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <h4 className="mb-0 d-flex align-items-center">
                                        <FaCubes className="text-primary me-2" />
                                        {t("stock.title")}
                                    </h4>

                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Entrepôt Cards */}
                    <Row className="mb-3">
                        {(entrepots.filter(e => e.libelle !== 'EXTERNE' && e.libelle !== 'RETOUR') ?? []).map((e) => (
                            <Col key={e.id} md={3} sm={6} xs={12}>
                                <EntrepotCard
                                    entrepot={e}
                                    active={selectedEntrepot?.value === e.id}
                                    onClick={() => handleClickCard(e.id)}
                                />
                            </Col>
                        ))}
                    </Row>
                    {/* Search + Export toolbar (full width) */}
                    <Row className="mb-4">
                        <Col>
                            <div className="d-flex gap-2 align-items-stretch">
                                <div className="flex-grow-1">
                                    <InputGroup className="shadow-sm">
                                        <InputGroupText>
                                            <FaSearch />
                                        </InputGroupText>
                                        <Input
                                            type="text"
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            placeholder={t("common.search") || "Rechercher un article ou un entrepôt..."}
                                        />
                                        {searchInput && (
                                            <Button color="light" onClick={() => setSearchInput("")}>
                                                <FaTimes />
                                            </Button>
                                        )}
                                    </InputGroup>
                                </div>

                                <div className="d-flex mx-3">
                                    <Button
                                        size="sm"
                                        color="success"
                                        className="shadow-sm d-flex align-items-center h-100"
                                        onClick={handleExportExcel}
                                        disabled={!filteredStocks || filteredStocks.length === 0}
                                        title={t("common.exportExcel") || "Exporter en Excel"}
                                    >
                                        <FaFileExcel className="me-2" />
                                        {t("common.exportExcel") || "Exporter en Excel"}
                                    </Button>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        className="mx-2"
                                        onClick={() => fileInputRef.current.click()}
                                    >
                                        <FaFileUpload className="me-2" size={12} />
                                        {t('stock.upload')}
                                    </Button>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileUpload}
                                        accept=".xlsx,.xls"
                                        style={{ display: 'none' }}
                                        disabled={!selectedEntrepot}

                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>
                    {/* Stocks Table */}
                    <Row>
                        <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
                            <Table bordered hover>
                                <thead>
                                    <tr>
                                        <th style={{ width: "250px" }}>{t("entrepot.libelle")}</th>
                                        <th >{t("product.description")}</th>
                                        <th style={{ width: "250px" }}>{t("product.stockDisponible")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!filteredStocks || filteredStocks.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="text-center py-4">
                                                <div className="text-muted">{t("stock.noStocks")}</div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredStocks.map((stock) => (
                                            <tr key={stock.id}>
                                                <td>{stock.entrepotNom}</td>
                                                <td>{stock.articleNom}</td>
                                                <td>{stock.quantiteDisponible}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Row>
                </Container>
            )}
        </>
    );
};

export default StockPage;
